import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Button,
  ButtonGroup
} from '@mui/material';

type SeasonKey = 'current' | 'upcoming' | 'planning';

interface SeasonalItem {
  id: number;
  name: string;
  status: string;
  statusColor: string;
  price: string;
  details: string;
  borderColor: string;
  opacity?: number;
}

type SeasonalData = Record<SeasonKey, SeasonalItem[]>;

const SeasonalPlanning: React.FC = () => {
  const [activeSeason, setActiveSeason] = useState<SeasonKey>('current');

  const seasonalData: SeasonalData = {
    current: [
      {
        id: 1,
        name: 'Sukuma Wiki',
        status: 'Abundant & Cheap',
        statusColor: '#28a745',
        price: 'KSh 20/kg',
        details: 'Available nationwide',
        borderColor: '#28a745'
      },
      {
        id: 2,
        name: 'Spinach',
        status: 'Good supply',
        statusColor: '#28a745',
        price: 'KSh 35/kg',
        details: 'Fresh from Central Kenya',
        borderColor: '#28a745'
      },
      {
        id: 3,
        name: 'Cabbage',
        status: 'Limited - Rains affected',
        statusColor: '#856404',
        price: 'KSh 45/kg',
        details: 'Quality concerns',
        borderColor: '#ffc107'
      },
      {
        id: 4,
        name: 'Beans',
        status: 'Excellent harvest',
        statusColor: '#28a745',
        price: 'KSh 120/kg',
        details: 'High protein quality',
        borderColor: '#28a745'
      },
      {
        id: 5,
        name: 'Tomatoes',
        status: 'Severely limited',
        statusColor: '#721c24',
        price: 'KSh 180/kg',
        details: 'Disease outbreak',
        borderColor: '#dc3545',
        opacity: 0.6
      },
      {
        id: 6,
        name: 'Maize',
        status: 'Store reserves good',
        statusColor: '#28a745',
        price: 'KSh 35/kg',
        details: 'Government subsidy',
        borderColor: '#28a745'
      }
    ],
    upcoming: [
      {
        id: 1,
        name: 'Sukuma Wiki',
        status: 'Will be scarce',
        statusColor: '#721c24',
        price: 'KSh 60/kg predicted',
        details: 'Plan alternatives',
        borderColor: '#dc3545',
        opacity: 0.6
      },
      {
        id: 2,
        name: 'Sweet Potatoes',
        status: 'Harvest season',
        statusColor: '#28a745',
        price: 'KSh 25/kg',
        details: 'Excellent for vitamins',
        borderColor: '#28a745'
      },
      {
        id: 3,
        name: 'Cassava',
        status: 'Drought resistant',
        statusColor: '#28a745',
        price: 'KSh 30/kg',
        details: 'Good carb source',
        borderColor: '#28a745'
      },
      {
        id: 4,
        name: 'Arrowroots',
        status: 'Peak season',
        statusColor: '#28a745',
        price: 'KSh 40/kg',
        details: 'Nutritious alternative',
        borderColor: '#28a745'
      }
    ],
    planning: [
      {
        id: 1,
        name: 'Green Grams',
        status: 'Planting season',
        statusColor: '#28a745',
        price: 'High protein crop',
        details: 'For next year',
        borderColor: '#28a745'
      },
      {
        id: 2,
        name: 'Traditional Vegetables',
        status: 'Encourage farming',
        statusColor: '#28a745',
        price: 'Managu, Terere',
        details: 'Nutritious options',
        borderColor: '#28a745'
      },
      {
        id: 3,
        name: 'Lentils',
        status: 'Planning next cycle',
        statusColor: '#28a745',
        price: 'Protein source',
        details: 'Drought tolerant',
        borderColor: '#28a745'
      }
    ]
  };

  const getSeasonalItems = (): SeasonalItem[] => {
    return seasonalData[activeSeason] || [];
  };

  return (
    <Card sx={{ mb: 3, boxShadow: '0 10px 30px rgba(0,0,0,0.2)', borderRadius: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
          Seasonal Planning & Availability
        </Typography>

        <Box sx={{ mb: 3 }}>
          <ButtonGroup variant="contained" fullWidth>
            <Button
              onClick={() => setActiveSeason('current')}
              sx={{
                backgroundColor: activeSeason === 'current' ? '#007bff' : '#f8f9fa',
                color: activeSeason === 'current' ? 'white' : '#333',
                borderRadius: '8px 0 0 8px',
                '&:hover': {
                  backgroundColor: activeSeason === 'current' ? '#0056b3' : '#e9ecef'
                }
              }}
            >
              Current Season (Long Rains)
            </Button>
            <Button
              onClick={() => setActiveSeason('upcoming')}
              sx={{
                backgroundColor: activeSeason === 'upcoming' ? '#007bff' : '#f8f9fa',
                color: activeSeason === 'upcoming' ? 'white' : '#333',
                '&:hover': {
                  backgroundColor: activeSeason === 'upcoming' ? '#0056b3' : '#e9ecef'
                }
              }}
            >
              Upcoming (Dry Season)
            </Button>
            <Button
              onClick={() => setActiveSeason('planning')}
              sx={{
                backgroundColor: activeSeason === 'planning' ? '#007bff' : '#f8f9fa',
                color: activeSeason === 'planning' ? 'white' : '#333',
                borderRadius: '0 8px 8px 0',
                '&:hover': {
                  backgroundColor: activeSeason === 'planning' ? '#0056b3' : '#e9ecef'
                }
              }}
            >
              Planning (Short Rains)
            </Button>
          </ButtonGroup>
        </Box>

        <Grid container spacing={2}>
          {getSeasonalItems().map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <Box
                sx={{
                  backgroundColor: '#f8f9fa',
                  p: 2,
                  borderRadius: 2,
                  borderLeft: `4px solid ${item.borderColor}`,
                  opacity: item.opacity || 1
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {item.name}
                </Typography>
                <Typography variant="body2" sx={{ color: item.statusColor, mb: 0.5 }}>
                  {item.status === 'Abundant & Cheap' && '✅ '}
                  {item.status === 'Good supply' && '✅ '}
                  {item.status === 'Excellent harvest' && '✅ '}
                  {item.status === 'Store reserves good' && '✅ '}
                  {item.status === 'Harvest season' && '✅ '}
                  {item.status === 'Drought resistant' && '✅ '}
                  {item.status === 'Peak season' && '✅ '}
                  {item.status === 'Planting season' && '✅ '}
                  {item.status === 'Encourage farming' && '✅ '}
                  {item.status === 'Planning next cycle' && '✅ '}
                  {item.status === 'Limited - Rains affected' && '⚠️ '}
                  {item.status === 'Will be scarce' && '❌ '}
                  {item.status === 'Severely limited' && '❌ '}
                  {item.status}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {item.price} • {item.details}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default SeasonalPlanning;
